// --------------------------------------------------------------
// Created On: 2024-05-14
// Author: KC Willard
//
// Last Modified: 2025-11-07
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./ShippingForm.module.scss";
import { CARRIER_NAMES } from "../../../constants/miscellaneous";

// Form for users to enter information about how they would like the products to be delivered.
export default function ShippingForm(props: Props): Component {
  // Reset input fields when ship via is changed.
  function handleChangeShippingCarrier(carrierSelection: string) {
    props.onChangeShippingBillingAccountNumber("");
    props.onChangeShipVia("");
    props.onChangeShippingCarrier(CARRIER_NAMES.find((carrier) => carrier.name === carrierSelection) || null);
  }

  return (
    <div className="px-3">
      <div>
        <div className={`${styles.carrierInput} col-3`}>
          <select
            data-test="shipping-carrier-selection"
            className="form-select"
            value={props.shippingCarrier === null ? "" : props.shippingCarrier.name}
            onChange={(e) => handleChangeShippingCarrier(e.target.value)}
          >
            <option value="">Please select a carrier...</option>
            {CARRIER_NAMES.map((carrier) => (
              <option value={carrier.name} key={carrier.name}>
                {carrier.name}
              </option>
            ))}
          </select>
        </div>
        {props.shippingCarrier !== null && props.shippingCarrier.name !== "Contact Customer" && (
          <div className={`${styles.carrierInput} col-3`}>
            <select
              data-test="ship-via-selection"
              className="form-select"
              value={props.shipVia}
              onChange={(e) => props.onChangeShipVia(e.target.value)}
            >
              <option value="">Please select a type...</option>
              {props.shippingCarrier.types.map((type) => (
                <option value={type.code} key={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {props.shippingCarrier !== null &&
          props.shippingCarrier.name !== "Best Way" &&
          props.shippingCarrier.name !== "Contact Customer" && (
            <div className={`${styles.carrierInput} col-3`}>
              <input
                className="form-control mx-auto"
                data-test="carrier-account-number-input"
                type="text"
                placeholder="Account #"
                value={props.shippingBillingAccountNumber}
                onChange={(e) => props.onChangeShippingBillingAccountNumber(e.target.value)}
              />
            </div>
          )}
      </div>
      <div className={`${styles.checkboxRow} form-check`}>
        <input
          className="form-check-big form-check-input"
          data-test="partial-shipment-checkbox-input"
          type="checkbox"
          checked={props.allowPartialShipments || false}
          onChange={() => props.onChangeAllowPartialShipments(!props.allowPartialShipments)}
          id="partialShipmentCheckbox"
        />
        <label className="form-check-label ps-2 pt-1 " htmlFor="partialShipmentCheckbox">
          Allow partial shipments
        </label>
      </div>
      <div className="pt-1 pb-1">
        <label style={{ fontSize: "large", fontWeight: "bold" }}>Special Instructions</label>
        <textarea
          className="form-control mx-auto"
          data-test="customer-comments-input"
          value={props.shippingSpecialInstructions}
          onChange={(e) => props.onChangeShippingSpecialInstructions(e.target.value)}
        />
      </div>
      <div className="pt-1 pb-1 ps-1 pe-1">
        <p style={{ fontSize: "x-small", fontWeight: "bold" }}>
          Shipping times above are IN ADDITION to the lead times provided for your order. If your package is over 150lb,
          the order will ship via freight. Expedited shipping is not allowed for orders over 150lbs.
        </p>
      </div>
    </div>
  );
}

interface Props {
  allowPartialShipments: boolean | null;
  shippingCarrier: Carrier | null;
  shipVia: string;
  shippingBillingAccountNumber: string;
  shippingSpecialInstructions: string;
  onChangeAllowPartialShipments: (allowPartialShipments: boolean) => void;
  onChangeShippingCarrier: (shippingCarrier: Carrier | null) => void;
  onChangeShipVia: (shipVia: string) => void;
  onChangeShippingBillingAccountNumber: (shippingBillingAccountNumber: string) => void;
  onChangeShippingSpecialInstructions: (shippingSpecialInstructions: string) => void;
}

interface Carrier {
  name: string;
  types: ShipVia[];
  billingType: string | null;
}

interface ShipVia {
  name: string;
  code: string;
}
